"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import KpResult, { type KpData } from "@/components/kp/KpResult";
import { generateKundali } from "@/lib/api/kundali";
import { safeSessionSet } from "@/lib/browser-storage";
import { toolErrorMessage } from "@/lib/tool-error-message";
import type { KundaliRequest } from "@ishvaram/api-client";

/** Short form and status labels; the intro sentences stay English. */
const KP_LABELS = {
  en: {
    eyebrow: "Free KP Calculator",
    submit: "Calculate KP Chart",
    computing: "Computing KP chart via Swiss Ephemeris...",
    method: "Applying Lahiri ayanamsa + Placidus cusps",
    reset: "Calculate for Another Person",
  },
  hi: {
    eyebrow: "मुफ्त KP कैलकुलेटर",
    submit: "KP चार्ट बनाएँ",
    computing: "Swiss Ephemeris से KP चार्ट की गणना हो रही है...",
    method: "लाहिरी अयनांश + प्लेसिडस भाव",
    reset: "किसी और के लिए गणना करें",
  },
} as const;

export default function KpAstrologyClient({ language = "en" }: { language?: "en" | "hi" }) {
  const labels = KP_LABELS[language];
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kpData, setKpData] = useState<KpData | null>(null);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    setKpData(null);

    try {
      const result = await generateKundali({ ...data, skip_interpretation: true });
      const kp = result.kp as KpData | undefined;
      if (!kp || kp.system !== "kp") {
        setError("KP data not available for this birth chart. Please try again.");
        return;
      }
      setKpData(kp);
      persistChatIntent();
      router.push(`/chat/${result.id}/?lang=${language}`);
    } catch (err) {
      setError(
        toolErrorMessage(
          err,
          language,
          language === "hi"
            ? "कुछ गलत हो गया — कृपया दोबारा कोशिश करें।"
            : "Something went wrong. Please try again."
        )
      );
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setKpData(null);
    setError(null);
  }

  return (
    <div className="card-sacred p-6 sm:p-8 mb-6">
      {/* Section header */}
      <div className="mb-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-2 text-intent-brand">
          {labels.eyebrow}
        </p>
        <h2 className="text-[22px] sm:text-[26px] font-bold leading-tight text-ish-dark font-serif">
          {language === "hi" ? "आपका KP उप-स्वामी विश्लेषण" : "Your KP Sub-Lord Analysis"}
        </h2>
        <p className="text-body mt-1 text-ish-text-muted">
          Enter your birth details to get the full four-level lord chart — cuspal sub-lords,
          planetary KP positions, and key readings for marriage, career, health, and wealth.
        </p>
        <p className="text-caption mt-2 text-ish-text-faint">
          Exact birth time required — KP cuspal sub-lords shift every ~4 minutes.
        </p>
      </div>

      {/* Form (shown until result arrives) */}
      {!kpData && (
        <div className="max-w-md">
          <BirthDetailsForm
            rememberBirthDetails
            onSubmit={handleSubmit}
            loading={loading}
            submitLabel={labels.submit}
            language={language}
            error={error ?? undefined}
            requireBirthTime={true}
            analyticsToolName="kp-astrology"
          />
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-10">
          <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto border-ish-accent" />
          <p className="mt-4 text-body text-ish-text-muted">{labels.computing}</p>
          <p className="mt-1 text-caption text-ish-text-faint">{labels.method}</p>
        </div>
      )}

      {/* Results */}
      {kpData && !loading && (
        <div className="space-y-6">
          <KpResult kp={kpData} />

          {/* Reset */}
          <div className="text-center">
            <button
              onClick={handleReset}
              className="text-body-small font-semibold text-ish-text-muted border border-ish-border rounded-lg px-6 py-2.5 hover:border-ish-accent hover:text-intent-brand transition-colors"
            >
              {labels.reset}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function persistChatIntent() {
  const sourcePath = typeof window === "undefined" ? "/kp-astrology" : window.location.pathname;
  safeSessionSet("seo_entry_intent", "chart_report");
  safeSessionSet("seo_entry_path", sourcePath || "/kp-astrology");
  safeSessionSet("seo_entry_page_category", "kundali");
  safeSessionSet("seo_entry_primary_offer", "report");
  safeSessionSet("seo_entry_report_plan_id", "brihat_kundali");
}
